import { type StructuredContentDocument } from '@repo/contracts/multi-use/structured-content/types';
import { css } from '@repo/pandacss/styled-system/css';
import { type CSSProperties } from 'react';
import { renderStructuredContent } from '../../rendererContext';
import type { RendererProps } from '../../types';
import { isSpacingObject } from '../../utils/isSpacingObject';
import { ALIGN_Y_MAP, COLORS_MAP, SPACING_TOKEN_MAP } from '../constants';
import type { AlignYToken, ColorToken, SpacingObject, SpacingToken } from '../types';

type ColumnsProps = StructuredContentDocument<{
  alignY?: AlignYToken;
  backgroundColor?: ColorToken;
  children: StructuredContentDocument;
  collapse?: boolean;
  padding?: SpacingToken;
  spacing?: SpacingObject | SpacingToken;
  rendererProps?: RendererProps;
}>;

export const Columns = ({ props }: ColumnsProps) => {
  const {
    alignY,
    backgroundColor,
    children,
    collapse = false,
    padding,
    spacing = { mobile: '3', desktop: '3-half' },
    rendererProps,
  } = props;

  const gapDesktop =
    SPACING_TOKEN_MAP[isSpacingObject(spacing) ? spacing.desktop : spacing] ?? SPACING_TOKEN_MAP['3-half'];
  const gapMobile =
    SPACING_TOKEN_MAP[isSpacingObject(spacing) ? spacing.mobile : spacing] ?? SPACING_TOKEN_MAP['3'];

  return (
    <div
      className={css({
        display: 'grid',
        gridTemplateColumns: {
          base: 'var(--columns-grid-template-columns-xs)',
          lg: 'repeat(12, minmax(0, 1fr))',
        },
        alignItems: 'var(--columns-align-items)',
        paddingTop: 'var(--columns-padding)',
        paddingRight: 'var(--columns-padding)',
        paddingBottom: 'var(--columns-padding)',
        paddingLeft: 'var(--columns-padding)',
        columnGap: { base: 'var(--columns-gap-xs)', lg: 'var(--columns-gap-lg)' },
        rowGap: { base: 'var(--columns-gap-xs)', lg: 'var(--columns-gap-lg)' },
        backgroundColor: 'var(--columns-background-color)',
      })}
      style={
        {
          '--columns-align-items': alignY ? ALIGN_Y_MAP[alignY] : undefined,
          '--columns-padding': SPACING_TOKEN_MAP[padding || 0],
          '--columns-gap-xs': gapMobile,
          '--columns-gap-lg': gapDesktop,
          '--columns-background-color': COLORS_MAP[backgroundColor || 'transparent'],
          '--columns-grid-template-columns-xs': collapse
            ? 'minmax(0, 1fr)'
            : 'repeat(12, minmax(0, 1fr))',
        } as CSSProperties
      }
    >
      {renderStructuredContent(children, {
        ...rendererProps,
        Column: { ...(rendererProps?.Column ?? {}), collapse },
      })}
    </div>
  );
};
